// Utility functions for loading content from JSON files

import events from '@/data/events.json';
import activities from '@/data/activities.json';
import blog from '@/data/blog.json';
import partners from '@/data/partners.json';

// Types
export interface Event {
  id: string;
  slug: string;
  title: string;
  date: string;
  type: string;
  image: string;
  description: string;
  location: string;
  time: string;
  points: string[];
  featured: boolean;
  inscriptionLink?: string;
  redirectToNotFound?: boolean;
  /** Mettre à true pour retirer un événement du site sans supprimer ses données. */
  hidden?: boolean;
}

export interface Activity {
  id: string;
  title: string;
  icon: string;
  category: string;
  description: string;
  classes: string[];
  subjects: string[];
  schedule: string;
  image: string;
  featured: boolean;
}

export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  date: string;
  author: string;
  authorRole: string;
  image: string;
  excerpt: string;
  content: string;
  category: string;
  tags: string[];
  featured: boolean;
}

export interface Partner {
  id: string;
  name: string;
  logo: string;
  category: string;
  description: string;
  website: string;
  featured: boolean;
}

// Events
// Les événements marqués `hidden` (brouillons, tests) sont exclus de tout le site :
// listes, pages de détail, sitemap. Leurs données restent dans events.json.
const visibleEvents: Event[] = (events as Event[]).filter(event => !event.hidden);

/** Minuit aujourd'hui : un événement qui a lieu aujourd'hui compte encore comme "à venir". */
function startOfToday(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

export function getAllEvents(): Event[] {
  return [...visibleEvents].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

export function getFeaturedEvents(): Event[] {
  return visibleEvents.filter(event => event.featured);
}

export function getEventBySlug(slug: string): Event | undefined {
  return visibleEvents.find(event => event.slug === slug);
}

export function getUpcomingEvents(limit?: number): Event[] {
  const today = startOfToday();
  const upcoming = visibleEvents
    .filter(event => new Date(event.date) >= today)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  return limit ? upcoming.slice(0, limit) : upcoming;
}

/** Événements déjà passés, du plus récent au plus ancien. */
export function getPastEvents(limit?: number): Event[] {
  const today = startOfToday();
  const past = visibleEvents
    .filter(event => new Date(event.date) < today)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return limit ? past.slice(0, limit) : past;
}

// Activities
export function getAllActivities(): Activity[] {
  return activities;
}

export function getFeaturedActivities(): Activity[] {
  return activities.filter(activity => activity.featured);
}

export function getActivityById(id: string): Activity | undefined {
  return activities.find(activity => activity.id === id);
}

// Blog
export function getAllBlogPosts(): BlogPost[] {
  return blog.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

export function getFeaturedBlogPosts(): BlogPost[] {
  return blog.filter(post => post.featured);
}

export function getBlogPostBySlug(slug: string): BlogPost | undefined {
  return blog.find(post => post.slug === slug);
}

export function getRecentBlogPosts(limit: number = 3): BlogPost[] {
  return getAllBlogPosts().slice(0, limit);
}

// Partners
export function getAllPartners(): Partner[] {
  return partners;
}

export function getFeaturedPartners(): Partner[] {
  return partners.filter(partner => partner.featured);
}

// Get previous and next blog posts
export function getAdjacentBlogPosts(currentSlug: string): { previous: BlogPost | null; next: BlogPost | null } {
  const posts = getAllBlogPosts();
  const currentIndex = posts.findIndex(post => post.slug === currentSlug);

  if (currentIndex === -1) {
    return { previous: null, next: null };
  }

  return {
    previous: currentIndex > 0 ? posts[currentIndex - 1] : null,
    next: currentIndex < posts.length - 1 ? posts[currentIndex + 1] : null,
  };
}

// Get previous and next events (only events with detail pages)
export function getAdjacentEvents(currentSlug: string): { previous: Event | null; next: Event | null } {
  // Filter out events that should redirect to not found
  const eventsWithDetails = getAllEvents().filter(event => !event.redirectToNotFound);
  const currentIndex = eventsWithDetails.findIndex(event => event.slug === currentSlug);

  if (currentIndex === -1) {
    return { previous: null, next: null };
  }

  return {
    previous: currentIndex > 0 ? eventsWithDetails[currentIndex - 1] : null,
    next: currentIndex < eventsWithDetails.length - 1 ? eventsWithDetails[currentIndex + 1] : null,
  };
}

// Utility functions
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

export function formatShortDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short'
  });
}
